import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, Home } from 'lucide-react';

/**
 * Full-page "Coming soon" screen for features we've scaffolded but
 * aren't ready to let users exercise yet (Crop Planner, Verify
 * Products, …). Lives at the route level — tiles / nav entries still
 * point here so the roadmap is visible, but tapping lands you on an
 * honest "not yet" page rather than a crashing feature.
 *
 * `titleKey` / `bodyKey` default to `common.comingSoon.*` and can be
 * overridden per route (e.g. the Planner page says what Planner will
 * one day do, instead of a generic blurb).
 */
export default function ComingSoonPage({
  titleKey = 'common.comingSoon.title',
  bodyKey = 'common.comingSoon.body',
  icon: Icon = Clock,
  dataTestId = 'coming-soon-page',
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="p-4 animate-fadeIn" data-testid={dataTestId}>
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading text-2xl font-bold">{t(titleKey)}</h1>
      </div>

      <Card>
        <CardContent className="flex flex-col items-center justify-center text-center py-16 px-6">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-farm-primary/10">
            <Icon className="h-8 w-8 text-farm-primary" />
          </div>
          <h2 className="text-xl font-semibold mb-3 text-farm-forest">
            {t('common.comingSoonPage.heading', { defaultValue: 'This feature is coming soon' })}
          </h2>
          <p className="text-muted-foreground mb-8 max-w-sm">{t(bodyKey)}</p>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button
              className="bg-farm-primary hover:bg-farm-primary-dark"
              onClick={() => navigate('/dashboard')}
            >
              <Home className="w-4 h-4 mr-2" />
              {t('common.comingSoonPage.goHome', { defaultValue: 'Back to home' })}
            </Button>
            <Button variant="outline" onClick={() => navigate(-1)}>
              {t('common.comingSoon.back')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
